import React, { useEffect, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import { CheckCircle, Error, HourglassEmpty, Visibility, VisibilityOff } from '@mui/icons-material';
import { Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import ErrorDisplay from '~/components/Common/ErrorDisplay';

import OffersService from '~/services/OffersService';
import { formatDateWithClock } from '~/services/TimeService';

import { titleCase } from '~/utils/stringUtils';

type Props = {
  user: App.User;
};

function CustomOfferStatusChip({ status }: { status: App.CustomOffer['status'] | string }) {
  const displayStatus = titleCase(status.replace(/_/g, ' '));

  switch (status) {
    case 'approved':
      return <Chip icon={<CheckCircle />} color="success" size="small" label={displayStatus} />;
    case 'draft':
      return <Chip icon={<HourglassEmpty />} color="warning" size="small" label={displayStatus} />;
    case 'declined':
      return <Chip icon={<Error />} color="error" size="small" label={displayStatus} />;
    default:
      return <Chip size="small" label={displayStatus} />;
  }
}

function getCustomOfferTags(customOffer: App.CustomOffer) {
  const tags: Array<string> = [];

  if (customOffer.items.some((item) => item.type === 'custom-offer-package')) {
    tags.push('Package');
  }

  if (customOffer.fk_package_custom_offer_id != null) {
    tags.push('Component Offer');
  }

  return tags;
}

function CustomOfferVisibilityIcon({ hiddenFromCustomer }: { hiddenFromCustomer?: boolean }) {
  const tooltipTitle = hiddenFromCustomer ? 'Not visible to customer in MyEscapes' : 'Visible to customer in MyEscapes';

  return (
    <Tooltip title={tooltipTitle}>
      {hiddenFromCustomer ? <VisibilityOff color="action" /> : <Visibility color="action" />}
    </Tooltip>
  );
}

const columns: Array<GridColDef<App.CustomOffer>> = [
  {
    field: 'name',
    headerName: 'Offer name',
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      const tags = getCustomOfferTags(params.row);

      return (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography>{params.row.name}</Typography>
          {tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Stack>
      );
    },
    display: 'flex',
  },
  {
    field: 'start_date',
    headerName: 'Start date',
    sortable: false,
    width: 150,
    valueGetter: (_value, row) => row.items[0].start_date,
    display: 'flex',
  },
  {
    field: 'due_date',
    headerName: 'Due date',
    sortable: false,
    width: 150,
    valueGetter: (_value, row) => row.due_date?.split('T')[0] ?? row.due_date,
    display: 'flex',
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    sortable: false,
    width: 180,
    valueGetter: (_value, row: App.CustomOffer) => formatDateWithClock(row.created_at),
    display: 'flex',
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    display: 'flex',
    renderCell: (params) => <CustomOfferStatusChip status={params.row.status} />,
  },
  {
    field: 'visibility',
    headerName: 'Visibility',
    sortable: false,
    width: 110,
    align: 'center',
    headerAlign: 'center',
    display: 'flex',
    renderCell: (params) => <CustomOfferVisibilityIcon hiddenFromCustomer={params.row.hidden_from_customer} />,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    display: 'flex',
    renderCell: (params) => (
      <Button variant="text" component={Link} to={`/users/${params.row.fk_customer_id}/custom-offers/${params.row.id}`}>
        Edit
      </Button>
    ),
  },
];

export default function CustomOffersTab({ user }: Props) {
  const userId = user.id_member;

  const [offers, setOffers] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState(null);

  const perPage = 25;

  useEffect(() => {
    setLoading(true);
    OffersService.getCustomOffers({ customerId: userId, page: currentPage + 1, perPage })
      .then((res) => {
        setOffers(res.result);
        setTotal(res.total);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [userId, currentPage, perPage]);

  return (
    <>
      {error && <ErrorDisplay message={error} />}

      <DataGrid
        loading={isLoading}
        columns={columns}
        rows={offers}
        rowCount={total}
        pagination
        paginationMode="server"
        paginationModel={{ page: currentPage, pageSize: perPage }}
        onPaginationModelChange={({ page }) => setCurrentPage(page)}
        slots={{ pagination: GridPagination }}
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        autoHeight
        sx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
            py: 1,
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            py: 1,
          },
          '& .MuiDataGrid-row': {
            maxHeight: 'none !important',
          },
          '& .MuiDataGrid-cellContent': {
            width: '100%',
          },
        }}
      />
    </>
  );
}
